// WorkspaceProvider — reads workspace memberships from the JWT's
// app_metadata.workspaces claim (written there by the auth-hook Worker).
//
// IMPORTANT: decode the JWT directly. supabase-js's
// `session.user.app_metadata` is the auth.users column, NOT the JWT
// claim — different sources, same name, easy mistake.
//
// The active workspace is held in localStorage so the user lands back
// in the same workspace after refresh. If the stored id is no longer a
// valid membership (e.g. user was removed), we fall back to the first
// available workspace.

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { readWorkspacesFromToken } from "@/lib/jwt";
import { supabase } from "@/lib/supabase";

export type WorkspaceRole = "owner" | "admin" | "editor" | "viewer";

export interface WorkspaceMembership {
  workspace_id: string;
  role: WorkspaceRole;
}

interface WorkspaceState {
  workspaces: WorkspaceMembership[];
  currentWorkspaceId: string | null;
  currentRole: WorkspaceRole | null;
  setCurrentWorkspaceId: (id: string | null) => void;
  loading: boolean;
}

const WorkspaceContext = createContext<WorkspaceState>({
  workspaces: [],
  currentWorkspaceId: null,
  currentRole: null,
  setCurrentWorkspaceId: () => {},
  loading: true,
});

const STORAGE_KEY = "mockingbird.activeWorkspaceId";
const RECENT_WORKSPACE_KEY = "mockingbird.recentWorkspace";
const SELECTION_REQUIRED_KEY = "mockingbird.workspaceSelectionRequired";
const RECENT_WORKSPACE_WINDOW_MS = 10 * 60 * 1000;

function readRecentWorkspace(): WorkspaceMembership | null {
  try {
    const raw = localStorage.getItem(RECENT_WORKSPACE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      workspace_id?: unknown;
      role?: unknown;
      created_at?: unknown;
    };
    const createdAt =
      typeof parsed.created_at === "number" ? parsed.created_at : 0;
    if (Date.now() - createdAt > RECENT_WORKSPACE_WINDOW_MS) {
      localStorage.removeItem(RECENT_WORKSPACE_KEY);
      return null;
    }
    if (
      typeof parsed.workspace_id === "string" &&
      ["owner", "admin", "editor", "viewer"].includes(String(parsed.role))
    ) {
      return {
        workspace_id: parsed.workspace_id,
        role: parsed.role as WorkspaceRole,
      };
    }
  } catch {
    return null;
  }
  return null;
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { session, loading: authLoading } = useAuth();
  const [currentWorkspaceId, _setCurrentWorkspaceId] = useState<string | null>(
    null,
  );
  const [fallbackWorkspaces, setFallbackWorkspaces] = useState<WorkspaceMembership[]>([]);
  const [fallbackLoading, setFallbackLoading] = useState(false);

  const claimWorkspaces = useMemo<WorkspaceMembership[]>(() => {
    return readWorkspacesFromToken(session?.access_token);
  }, [session]);

  useEffect(() => {
    let cancelled = false;
    if (!session?.user.id || claimWorkspaces.length > 0) {
      setFallbackWorkspaces([]);
      setFallbackLoading(false);
      return;
    }

    setFallbackLoading(true);
    void (async () => {
      try {
        const { data, error } = await supabase
          .from("workspace_members")
          .select("workspace_id, role")
          .eq("user_id", session.user.id)
          .not("accepted_at", "is", null);
        if (cancelled) return;
        if (error) {
          console.warn("[workspace] membership fallback failed:", error.message);
          setFallbackWorkspaces([]);
          setFallbackLoading(false);
          return;
        }
        setFallbackWorkspaces(
          (data ?? []).filter((row): row is WorkspaceMembership => {
            return (
              typeof row.workspace_id === "string" &&
              ["owner", "admin", "editor", "viewer"].includes(row.role)
            );
          }),
        );
        setFallbackLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.warn("[workspace] membership fallback failed:", error);
        setFallbackWorkspaces([]);
        setFallbackLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.user.id, claimWorkspaces.length]);

  const recentWorkspace = readRecentWorkspace();
  const baseWorkspaces =
    claimWorkspaces.length > 0 ? claimWorkspaces : fallbackWorkspaces;
  const workspaces =
    recentWorkspace &&
    !baseWorkspaces.some((w) => w.workspace_id === recentWorkspace.workspace_id)
      ? [recentWorkspace, ...baseWorkspaces]
      : baseWorkspaces;

  useEffect(() => {
    if (authLoading) return;
    if (workspaces.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(RECENT_WORKSPACE_KEY);
      localStorage.removeItem(SELECTION_REQUIRED_KEY);
      _setCurrentWorkspaceId(null);
      return;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    const validStored =
      stored && workspaces.some((w) => w.workspace_id === stored)
        ? stored
        : null;
    if (!validStored && localStorage.getItem(SELECTION_REQUIRED_KEY) === "1") {
      _setCurrentWorkspaceId(null);
      return;
    }
    _setCurrentWorkspaceId(validStored ?? workspaces[0]!.workspace_id);
  }, [authLoading, workspaces]);

  const setCurrentWorkspaceId = (id: string | null) => {
    if (!id) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(RECENT_WORKSPACE_KEY);
      localStorage.setItem(SELECTION_REQUIRED_KEY, "1");
      _setCurrentWorkspaceId(null);
      return;
    }
    if (!workspaces.some((w) => w.workspace_id === id)) return;
    localStorage.setItem(STORAGE_KEY, id);
    localStorage.removeItem(SELECTION_REQUIRED_KEY);
    _setCurrentWorkspaceId(id);
  };

  const currentRole = useMemo<WorkspaceRole | null>(() => {
    if (!currentWorkspaceId) return null;
    return (
      workspaces.find((w) => w.workspace_id === currentWorkspaceId)?.role ??
      null
    );
  }, [currentWorkspaceId, workspaces]);

  const loading = authLoading || fallbackLoading;

  const value = useMemo<WorkspaceState>(
    () => ({
      workspaces,
      currentWorkspaceId,
      currentRole,
      setCurrentWorkspaceId,
      loading,
    }),
    [workspaces, currentWorkspaceId, currentRole, loading],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  return useContext(WorkspaceContext);
}

export function canEdit(role: WorkspaceRole | null): boolean {
  return role === "owner" || role === "admin" || role === "editor";
}

export function canAdmin(role: WorkspaceRole | null): boolean {
  return role === "owner" || role === "admin";
}
